import {compact} from '@benchling/js-utils';

import isArchived from 'archive/isArchived';
import {assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {getTagSchemaForStructuredTable} from 'notebook/integrations/registry/StructuredTableUtilsForRegistration';
import {hasUnsubmittedValues} from 'notebook/notes/plugins/fill-in-text-box/FillInTextBoxUtils';
import {
  ProcessTaskStatusStructuredTableColumn,
  StructuredTableKind,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import {areRowValuesEquivalent} from 'notebook/structured-tables/util/addRowsToTable';
import getBlankRow from 'notebook/structured-tables/util/getBlankRow';
import tableHasUnsubmittedChanges from 'notebook/structured-tables/util/tableHasUnsubmittedChanges';
import {hasLoadingCells} from 'notebook/tables/excess/Util';
import {BaseTypedValueType, TableColumnType} from 'notebook/tables/TableConstants';
import {isNoteFillInTextBox, isStructuredTable} from 'notebook/TypePredicates';
import {EntryNote} from 'notebook/util/NoteUtilConstants';
import {DefaultStatusDisplayName} from 'processes/ProcessConstants';
import {isTerminalStatus} from 'processes/status/TaskStatusUtil';
import {ProcessOutputStructuredTable} from 'processes/tables/ProcessOutputStructuredTableAdapter';
import {ProcessTaskStructuredTable} from 'processes/tables/ProcessTaskStructuredTableAdapter';

export function tableUsesArchivedSchema(structuredTable: UnknownStructuredTable): boolean {
  // don't check for unsubmitted changes on registration tables for an archived entity schema
  // (was one of many issues blocking entry review in CUST-3990, see PR#85798 for more details)
  if (structuredTable.kind === StructuredTableKind.REGISTRATION) {
    const schema = getTagSchemaForStructuredTable(structuredTable);
    if (schema && isArchived(schema)) {
      return true;
    }
  }

  return false;
}

/**
 * In some cases the Output Table in an entry should not block the send-for-review action. The table is protected
 * from deletion (since it cannot be re-added), but not all tasks yield outputs. In particular, in the case
 * of failed or cancelled tasks the operator may be blocked from sending for review since the empty table cannot
 * be submitted nor deleted.
 *
 * @param note The EntryNote that contains the task and output tables
 * @returns boolean flag signaling if we should ignore author action checks on the output table
 */
export function shouldIgnoreOutputTable(note: EntryNote): boolean {
  const outputTableNoteParts = note.noteParts.filter(
    ({item}) => item != null && isStructuredTable(item) && item.kind === StructuredTableKind.PROCESS_OUTPUT
  );
  if (outputTableNoteParts.length === 0) {
    // This entry has no output table
    return false;
  }
  assertAndContinue(
    outputTableNoteParts.length === 1,
    'Expected to find only one output table in execution entry.'
  );
  const outputTable = outputTableNoteParts[0].item as ProcessOutputStructuredTable;

  if (
    outputTable.rows.length !== 1 ||
    !areRowValuesEquivalent(outputTable.rows[0].cells, getBlankRow(0, outputTable.columns, null))
  ) {
    // If the output table is not empty we can't ignore the check
    return false;
  }

  // When the output table is empty (only a single default row) we can ignore iff all tasks have a teminal status
  const taskTableNoteParts = note.noteParts.filter(
    ({item}) => item != null && isStructuredTable(item) && item.kind === StructuredTableKind.PROCESS_TASK
  );
  assertAndContinue(
    taskTableNoteParts.length === 1,
    'Expected to find only one task table in execution entry.'
  );
  const taskTable = taskTableNoteParts[0].item as ProcessTaskStructuredTable;

  const statusColumnIndex = taskTable.columns.findIndex(
    (column) => column.columnType === TableColumnType.PROCESS_TASK_STATUS
  );

  const statusLifecycle = (taskTable.columns[statusColumnIndex] as ProcessTaskStatusStructuredTableColumn)
    .frontEndMetadata?.statusLifecycle;

  return taskTable.rows.every((row) => {
    const typedValue = row.cells[statusColumnIndex].typedValue;
    return (
      typedValue != null &&
      statusLifecycle != null &&
      typedValue.type === BaseTypedValueType.TASK_STATUS &&
      (isTerminalStatus(typedValue.id, statusLifecycle) ||
        typedValue.displayName === DefaultStatusDisplayName.COMPLETED_AFTER_REVIEW)
    );
  });
}

function hasUnsubmittedTextBoxChanges(note: EntryNote): boolean {
  return (
    note.noteParts.find(
      ({item}) => item != null && isNoteFillInTextBox(item) && hasUnsubmittedValues(item)
    ) != null
  );
}

export interface StructuredTableSubmissionStatus {
  name: string;
  kind: string;
  provenanceUuid: string;
  submitted: boolean;
}

export function getTablesSubmissionStates(note: EntryNote): Array<StructuredTableSubmissionStatus> {
  const computedTables = note.noteRuntimeSnapshot.evaluationSnapshot;
  if (computedTables == null) {
    return [];
  }

  return compact(
    note.noteParts.map(({item}) => {
      if (item == null) {
        return null;
      }
      if (!isStructuredTable(item)) {
        return null;
      }
      if (item.kind === StructuredTableKind.PROCESS_OUTPUT && shouldIgnoreOutputTable(note)) {
        // Don't check output tables when all tasks have a terminal status
        return null;
      }

      return {
        name: item.name,
        kind: item.kind,
        provenanceUuid: item.provenanceUuid,
        submitted:
          tableUsesArchivedSchema(item) || !tableHasUnsubmittedChanges(item, computedTables[item.name]),
      };
    })
  );
}

function hasUnsubmittedTableChanges(note: EntryNote): boolean {
  return getTablesSubmissionStates(note).some((status) => !status.submitted);
}

export enum UnsubmittedDataError {
  NONE,
  TABLE_CELLS_LOADING,
  UNSUBMITTED_TABLES,
  UNSUBMITTED_TEXT_BOXES,
}

export function checkUnsubmittedDataErrors(note: EntryNote): UnsubmittedDataError {
  const computedTables = note.noteRuntimeSnapshot.evaluationSnapshot;
  if (hasUnsubmittedTableChanges(note)) {
    return UnsubmittedDataError.UNSUBMITTED_TABLES;
  } else if (BenchlingConfig.ELN_ENABLE_TEXT_BOX_DATA_MODIFICATIONS && hasUnsubmittedTextBoxChanges(note)) {
    return UnsubmittedDataError.UNSUBMITTED_TEXT_BOXES;
  } else if (computedTables != null && hasLoadingCells(computedTables)) {
    return UnsubmittedDataError.TABLE_CELLS_LOADING;
  }
  return UnsubmittedDataError.NONE;
}

/**
 * This function determines whether we should disable author actions.
 * Author actions are defined as self-approval or send-for-review on an entry.
 * Author actions should be disabled if either the entry has unsubmitted tables or table cells are still loading.
 */
export function shouldDisableAuthorActions(note: EntryNote | undefined): {
  disableAuthorActions: boolean;
  disableAuthorActionsTooltip: string | null;
} {
  const computedTables = note?.noteRuntimeSnapshot.evaluationSnapshot;
  let disableAuthorActions = false;
  let disableAuthorActionsTooltip = null;
  if (note === undefined) {
    disableAuthorActions = true;
    disableAuthorActionsTooltip = 'Document not yet loaded';
  } else if (computedTables != null && BenchlingConfig.ELN_SEND_REVIEW_REQUIRES_SUBMITTED_TABLES) {
    const unsubmittedDataError = checkUnsubmittedDataErrors(note);
    disableAuthorActions = unsubmittedDataError !== UnsubmittedDataError.NONE;
    switch (unsubmittedDataError) {
      case UnsubmittedDataError.UNSUBMITTED_TABLES:
        disableAuthorActionsTooltip = 'Please submit all tables before initiating a review';
        break;
      case UnsubmittedDataError.TABLE_CELLS_LOADING:
        disableAuthorActionsTooltip = 'Please wait for all tables cells to load before initiating a review';
        break;
      case UnsubmittedDataError.UNSUBMITTED_TEXT_BOXES:
        disableAuthorActionsTooltip = 'Please submit all text boxes before initiating a review';
        break;
      default:
        break;
    }
  }
  return {disableAuthorActions, disableAuthorActionsTooltip};
}
